import React, { useEffect, useState } from "react";
import { Layout, Menu, Avatar, Dropdown } from "antd";
import { Outlet } from "react-router-dom";

import {
  UserOutlined,
  LogoutOutlined,
  DockerOutlined,
  MailOutlined,
  BellOutlined,
  SecurityScanOutlined,
  SettingOutlined,
} from "@ant-design/icons";

import "../css/common.css";
import { post } from "../../services/services";
import { ApiURL } from "../../services/apiConstants";
import NotificationModal from "../../components/NotificationModal";
import ForgateCodeModal from "../../components/ForgateCodeModal";
import FloatingFabStaffModal from "./FloatingFabStaffModal";
import ChatBot from "../../components/ChatBot";
import SidebarNavigation from "../../components/navigation/SidebarNavigation";
import HeaderTimeWidget from "../../components/time/HeaderTimeWidget";
import useSubscriptionAccess from "../../hooks/useSubscriptionAccess";
import SubscriptionAccessScreen from "../../components/subscription/SubscriptionAccessScreen";

const { Header, Sider, Content } = Layout;

/* 🔥 MOVE ICON MAP HERE — after all imports */
const StaffDashboardLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [userData, setUserData] = useState({});
  const [unreadCount, setUnreadCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const access = useSubscriptionAccess(ApiURL.staff_modules_st);

  useEffect(() => {
    const localUser = JSON.parse(localStorage.getItem("user_data"));
    if (localUser?.data) setUserData(localUser.data);
  }, []);

  // Logout function
  const logout = () => {
    const preserved = Object.keys(localStorage)
      .filter((key) => key.startsWith("security_code_lock_time_"))
      .map((key) => [key, localStorage.getItem(key)]);
    localStorage.clear();
    preserved.forEach(([key, value]) => localStorage.setItem(key, value));
    post(ApiURL.logout, new FormData()).finally(() => {
      window.location = "/";
    });
  };

  const userMenu = (
    <Menu>
      <Menu.Item key="time-reference" className="marine-header__time-menu">
        <HeaderTimeWidget variant="menu" showSelector={false} />
      </Menu.Item>
      <Menu.Item key="time-date" icon={<SettingOutlined />}>
        <a href="/utilities/settings/time-date">Settings · Time &amp; Date</a>
      </Menu.Item>
      <Menu.Item onClick={logout} key="logout" icon={<LogoutOutlined />}>
        Logout
      </Menu.Item>
    </Menu>
  );

  if (!access.hasAccess) {
    return <SubscriptionAccessScreen access={access} companyName={access.data?.company_details?.name} onLogout={logout} />;
  }

  return (
    <>
      <Layout style={{ height: "100vh", overflow: "hidden" }}>
        <style>
          {`
            .no-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .no-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            /* Flex Column Helper */
            .flex-column {
              display: flex;
              flex-direction: column;
              height: 100%;
            }
            /* Scroll Container Helper */
            .scroll-container {
              flex: 1;
              overflow-y: auto;
            }
          `}
        </style>
        <Sider
          width={230}
          collapsible
          collapsed={collapsed}
          onCollapse={() => setCollapsed(!collapsed)}
          breakpoint="lg"
          collapsedWidth={72}
          className="custom-sider"
          style={{ height: "100vh", background: "linear-gradient(180deg, #08213c 0%, #041628 100%)" }}
        >
          <div className="flex-column">
            <div className="logo" style={{ flexShrink: 0, background: '#091e31', padding: '20px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <h2 style={{ color: '#fff', margin: 0, fontSize: '20px', fontWeight: '800', letterSpacing: '1px' }}>MARIN-STAFF</h2>
            </div>

            <div className="scroll-container no-scrollbar">
              <SidebarNavigation modules={access.subscription?.modules || []} collapsed={collapsed} />
              <button
                type="button"
                onClick={logout}
                style={{
                  width: "calc(100% - 24px)",
                  margin: "0 12px 20px",
                  padding: "10px 12px",
                  border: "1px solid rgba(255,255,255,0.16)",
                  borderRadius: 7,
                  background: "transparent",
                  color: "#dbeafe",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <LogoutOutlined style={{ marginRight: collapsed ? 0 : 10 }} />
                {!collapsed && "Logout"}
              </button>
            </div>
          </div>
        </Sider>

        <Layout className="site-layout flex-column" style={{ height: "100vh" }}>
          <Header
            style={{
              padding: "0 24px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexShrink: 0,
              backgroundColor: "#f8fafc",
              boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
              zIndex: 10,
            }}
          >
            <div
              className="marine-header__metadata"
              style={{
                color: "#001529",
                display: "flex",
                alignItems: "center",
                fontSize: "15px"
              }}
            >
              <MailOutlined style={{ marginRight: 8, color: "#E11236" }} /> <b>Email:</b>{" "}
              <span style={{ marginLeft: 6 }}>{userData?.email}</span>
              <DockerOutlined style={{ marginLeft: 20, marginRight: 8, color: "#E11236", fontSize: "18px" }} />{" "}
              <b>Ship:</b> <span style={{ marginLeft: 6 }}>{userData?.ship_details?.vessel_name}</span>
              <UserOutlined style={{ marginLeft: 20, marginRight: 8, color: "#E11236" }} />{" "}
              <b>Designation:</b> <span style={{ marginLeft: 6 }}>{userData?.designation}</span>
              <HeaderTimeWidget />
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 20,
                color: "#001529",
              }}
            >
              {(userData?.designation === "Captain" ||
                userData?.designation === "Chief Engineer") && (
                <SecurityScanOutlined
                  style={{ fontSize: 20, cursor: "pointer", color: "#E11236" }}
                  onClick={() => setIsCodeModalOpen(true)}
                />
              )}

              <div
                style={{ position: "relative", cursor: "pointer", display: "flex", alignItems: "center" }}
                onClick={() => setIsModalOpen(true)}
              >
                <BellOutlined style={{ fontSize: 22, color: "#E11236" }} />
                {unreadCount > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -8,
                      background: "#E11236",
                      color: "white",
                      borderRadius: "50%",
                      fontSize: 10,
                      minWidth: 18,
                      height: 18,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "bold",
                      border: "2px solid #fff"
                    }}
                  >
                    {unreadCount}
                  </span>
                )}
              </div>

              <Dropdown overlay={userMenu} placement="bottomRight">
                <div style={{ textTransform: "capitalize", cursor: "pointer", display: "flex", alignItems: "center", fontSize: "15px" }}>
                  <b>Welcome:</b> <span style={{ marginLeft: 6, marginRight: 12 }}>{userData?.name}</span>{" "}
                  <Avatar style={{ backgroundColor: '#001529' }} icon={<UserOutlined />} />
                </div>
              </Dropdown>
            </div>
          </Header>

          {/* <Content style={{ margin: "0 16px" }}>
            <div style={{ padding: 15, minHeight: 360 }}>
              <Outlet />
            </div>
          </Content> */}
          <Content className="scroll-container no-scrollbar" style={{ margin: "0 16px", position: "relative" }}>
            <div style={{ padding: 15, minHeight: 360 }}>
              <Outlet />
            </div>

            {/* ADD FAB HERE */}
            <FloatingFabStaffModal />
            <ChatBot apiUrl={ApiURL.staff_chatbot} />
          </Content>
        </Layout>
      </Layout>

      <NotificationModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        handleUnreadCountChange={setUnreadCount}
      />
      <ForgateCodeModal
        isCodeModalOpen={isCodeModalOpen}
        setCodeIsModalOpen={setIsCodeModalOpen}
      />
    </>
  );
};

export default StaffDashboardLayout;
